import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import apis from '../../../services/Apis';
import { SecureGet, SecurePatch } from '../../../services/axiosCall';
import {
    Card,
    Flex,
    Typography,
    message,
    Table,
    Button,
    Avatar,
} from 'antd';
import {
    FileTextOutlined,
    CheckCircleOutlined,
    MessageOutlined,
    UserOutlined,
    CrownOutlined,
    TrophyOutlined,
} from '@ant-design/icons';
import { table } from './struct';
import { CustomDatePicker } from '../../../components/CustomDatePicker';
import { useSelector } from 'react-redux';
import Counter from './components/Counter';
import { TableCol } from './tableCol';
import useEvaluationRequestScope from '../../../hooks/useEvaluationRequestScope';
import CommentsModal from './components/CommentsModal';

const { Title, Text } = Typography;

const FacultyDetails = () => {
    const { state } = useLocation();
    const { id: routeId } = useParams();
    const { row: routeRow, year } = state || { row: null, year: null };

    // Fallback so a hard refresh on /faculty/:id (no location.state) still works.
    const row = routeRow?.id === routeId ? routeRow : { id: routeId };
    const facultyId = routeId;

    const user = useSelector((state) => state.user);
    const { userDetails } = user;

    const navigate = useNavigate();

    const [messageApi, contextHolder] = message.useMessage();
    const apa = useSelector((state) => state.apa);
    const { apaData } = apa;

    const [planningData, setPlanningData] = useState([]);
    const [planningLoading, setPlanningLoading] = useState(true);
    const [planningCount, setPlanningCount] = useState({});
    const [resultData, setResultData] = useState(null);
    const [resultLoading, setResultLoading] = useState(true);

    const currentDate = new Date();
    const currentYear = currentDate.getFullYear() - 1;
    const [selectedYear, setSelectedYear] = useState((routeRow?.id === routeId && Number(year)) || Number(currentYear));

    const scope = useEvaluationRequestScope(JSON.stringify([facultyId, selectedYear]));

    const [activeModal, setActiveModal] = useState(null); // 'head' | 'dean' | 'vc' | null

    // Derived from resultData — no separate state needed
    const commentsStore = {
        head: resultData?.comment_by_head ?? null,
        dean: resultData?.comment_by_dean ?? null,
        vc: resultData?.comment_by_vc ?? null,
    };

    const handleSelectedYear = (_, yearString) => {
        setPlanningData([]);
        setPlanningCount({});
        setResultData(null);
        setPlanningLoading(true);
        setResultLoading(true);
        setActiveModal(null);
        setSelectedYear(Number(yearString));
    };

    // ── Planning list (used to resolve the "View" click per form) ──
    const getApaPlanningAdmin = useCallback(() => {
        const isCurrent = scope.capture();
        if (!facultyId) {
            setPlanningData([]);
            setPlanningLoading(false);
            return;
        }
        setPlanningData([]);
        setPlanningLoading(true);
        SecureGet({
            url: `${apis.PLANNING_ADMIN}`,
            params: { year: selectedYear, user: facultyId, limit: 1000 }, // limit avoids silent pagination cutoff
        })
            .then((response) => {
                if (!isCurrent()) return;
                setPlanningData(response.data?.results ?? []);
            })
            .catch(() => {
                if (!isCurrent()) return;
                setPlanningData([]);
            })
            .finally(() => { if (isCurrent()) setPlanningLoading(false); });
    }, [selectedYear, facultyId]);

    // ── Completed planning counter ──
    const getCompletedPlanning = useCallback(() => {
        const isCurrent = scope.capture();
        if (!facultyId) {
            setPlanningCount({});
            return;
        }
        SecureGet({ url: `${apis.COMPLETED_PLANNING_MODULE}/${facultyId}/${selectedYear}` })
            .then((response) => { if (isCurrent()) setPlanningCount(response.data ?? {}); })
            .catch(() => { if (isCurrent()) setPlanningCount({}); });
    }, [selectedYear, facultyId]);

    // ── APA result (points + comments) ──
    const fetchResult = useCallback(() => {
        const isCurrent = scope.capture();
        if (!facultyId) {
            setResultData(null);
            setResultLoading(false);
            return;
        }
        setResultData(null);
        setResultLoading(true);
        SecureGet({
            url: `${apis.APA_RESULTS_FACULTY_ADMIN}`,
            params: { year: selectedYear, user: facultyId },
        })
            .then((res) => { if (isCurrent()) setResultData(res.data?.results?.[0] ?? null); })
            .catch(() => {
                if (!isCurrent()) return;
                setResultData(null);
            })
            .finally(() => { if (isCurrent()) setResultLoading(false); });
    }, [selectedYear, facultyId]);

    useEffect(() => {
        fetchResult();
    }, [fetchResult]);

    useEffect(() => {
        getApaPlanningAdmin();
        getCompletedPlanning();
    }, [getApaPlanningAdmin, getCompletedPlanning]);

    const handlePlanningClick = (item) => {
        if (!facultyId) {
            messageApi.error('Missing faculty reference — please reopen this page from the list.');
            return;
        }
        if (planningLoading) {
            messageApi.loading('Planning data is still loading, please try again in a moment.');
            return;
        }

        const temp = planningData.find(
            (obj) => (obj?.apa_form?.id ?? obj?.apa_form) === item?.id
        );

        if (!temp) {
            console.warn('[planning] no match for form', {
                formNo: item?.form_no,
                formId: item?.id,
                availableFormIds: planningData.map((o) => o?.apa_form?.id ?? o?.apa_form),
                planningCount: planningData.length,
            });
            messageApi.info(`No planning submitted for form ${item?.form_no ?? ''}.`);
            return;
        }

        navigate(`/faculty/${facultyId}/planning/${item?.form_no}`, { state: { ...item, ...temp } });
    };

    const handleEvaluationClick = (item) => {
        if (!facultyId) {
            messageApi.error('Missing faculty reference — please reopen this page from the list.');
            return;
        }
        navigate(`/faculty/${facultyId}/evaluation/${item?.form_no}`, {
            state: { ...item, rowUser: facultyId, year: selectedYear },
        });
    };

    const handleAddComment = async (type, text) => {
        const isCurrent = scope.capture();
        if (!resultData?.id) {
            messageApi.error('No result record found for this faculty.');
            return;
        }

        await SecurePatch({
            url: `${apis.APA_RESULT_COMMENT}/${resultData.id}`,
            data: { comment: text },
        })
            .then(() => {
                if (!isCurrent()) return;
                messageApi.success('Comment saved successfully.');
                fetchResult(); // refresh so commentsStore reflects the new value
                setActiveModal(null);
            })
            .catch((err) => {
                console.error('[comment] save failed', err);
                messageApi.error('Failed to save comment. Please try again.');
            });
    };

    const columns = [
        ...TableCol(),
        {
            title: 'Planning',
            key: 'view_planning',
            align: 'center',
            render: (_, record) => (
                <Button
                    type="link"
                    icon={<FileTextOutlined />}
                    disabled={planningLoading}
                    style={{ padding: 0, height: 'auto', fontWeight: 500, color: '#7B61FF' }}
                    onClick={() => handlePlanningClick(record)}
                >
                    View
                </Button>
            ),
        },
        {
            title: 'Evaluation',
            key: 'view_evaluation',
            align: 'center',
            render: (_, record) => (
                <Button
                    type="link"
                    icon={<CheckCircleOutlined />}
                    style={{ padding: 0, height: 'auto', fontWeight: 500, color: '#52A152' }}
                    onClick={() => handleEvaluationClick(record)}
                >
                    View
                </Button>
            ),
        },
    ];

    const CARD_CONFIG = {
        head: {
            border: '1px solid #E4DEFF',
            background: '#F9F8FF',
            avatarBg: '#7B61FF',
            accent: '#7B61FF',
            label: 'Head Comment',
            icon: <UserOutlined />,
        },
        dean: {
            border: '1px solid #F0E0B0',
            background: '#FFFBF0',
            avatarBg: '#B8860B',
            accent: '#B8860B',
            label: 'Dean Comment',
            icon: <CrownOutlined />,
        },
        vc: {
            border: '1px solid #F5C6C6',
            background: '#FFF0F0',
            avatarBg: '#C62828',
            accent: '#C62828',
            label: 'VC Comment',
            icon: <TrophyOutlined />,
        },
    };

    const CommentSummaryCard = ({ type }) => {
        const comment = commentsStore[type]; // string | null
        const cfg = CARD_CONFIG[type];

        return (
            <Card
                hoverable
                onClick={() => setActiveModal(type)}
                style={{
                    borderRadius: 14,
                    border: cfg.border,
                    background: cfg.background,
                    flex: 1,
                    minWidth: 260,
                }}
                bodyStyle={{ padding: 16 }}
            >
                <Flex gap={10} align="center">
                    <Avatar icon={cfg.icon} style={{ backgroundColor: cfg.avatarBg }} />
                    <Text strong>{cfg.label}</Text>
                </Flex>

                {comment ? (
                    <Text
                        type="secondary"
                        style={{
                            display: 'block',
                            marginTop: 10,
                            fontSize: 13,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {comment}
                    </Text>
                ) : (
                    <Text type="secondary" style={{ display: 'block', marginTop: 10, fontSize: 13 }}>
                        No comment yet — click to add one.
                    </Text>
                )}

                <Button
                    type="link"
                    icon={<MessageOutlined />}
                    style={{ padding: 0, marginTop: 10, color: cfg.accent }}
                >
                    {comment ? 'View & Edit' : 'Add Comment'}
                </Button>
            </Card>
        );
    };

    const filteredForms = apaData?.filter((form) => form?.form_no !== '1.1.B');

    return (
        <Card
            {...table.wrapper}
            style={{
                borderRadius: 16,
                boxShadow: '0 2px 12px rgba(123, 97, 255, 0.08)',
                border: '1px solid #EFEAFF',
                ...table.wrapper?.style,
            }}
        >
            {contextHolder}
            <Flex
                wrap="wrap"
                justify="space-between"
                align="center"
                style={{
                    background: 'linear-gradient(135deg, #F4F2FF 0%, #FFFFFF 100%)',
                    borderRadius: 14,
                    padding: '18px 22px',
                    marginBottom: 20,
                    border: '1px solid #EAE4FF',
                }}
            >
                <Flex vertical>
                    <Title level={3} style={{ margin: 0, color: '#3D2B8C' }}>
                        {row?.name}&apos;s Planning &amp; Evaluation
                    </Title>
                    <Text type="secondary">Track planning, evaluation, and review comments</Text>
                </Flex>
                <Counter planningCount={planningCount} />
            </Flex>
            <br />

            <Flex>
                {!resultLoading && !resultData && <Text>No evaluation data available.</Text>}
                {/* APA Results Section */}
                {!resultLoading && resultData && (
                    <Card
                        style={{
                            borderRadius: 14,
                            border: '1px solid #EAE4FF',
                            background: '#FAFBFF',
                            marginBottom: 20,
                        }}
                        bodyStyle={{ padding: '20px 24px' }}
                    >
                        <Text style={{ fontSize: 12, color: '#8B8FA8', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 500 }}>
                            APA Points — {resultData.year}
                        </Text>

                        <Flex gap={12} wrap="wrap" style={{ marginTop: 14, marginBottom: 16 }}>
                            {[
                                { label: 'Teaching', value: resultData.teaching_apa_point, color: '#7B61FF' },
                                { label: 'Research', value: resultData.research_apa_point, color: '#3D2B8C' },
                                { label: 'Service', value: resultData.service_apa_point, color: '#3D2B8C' },
                            ].map(({ label, value, color }) => (
                                <div
                                    key={label}
                                    style={{
                                        background: '#F4F2FF',
                                        borderRadius: 8,
                                        padding: '12px 20px',
                                        flex: 1,
                                        minWidth: 120,
                                    }}
                                >
                                    <Text style={{ fontSize: 12, color: '#8B8FA8', display: 'block', marginBottom: 4 }}>{label}</Text>
                                    <Text style={{ fontSize: 22, fontWeight: 500, color }}>{value}</Text>
                                </div>
                            ))}
                        </Flex>

                        <Flex
                            align="center"
                            gap={10}
                            style={{
                                background: '#EAE4FF',
                                borderRadius: 8,
                                padding: '12px 18px',
                                border: '1px solid #D6CEFF',
                            }}
                        >
                            <TrophyOutlined style={{ fontSize: 18, color: '#7B61FF' }} />
                            <Text style={{ flex: 1, fontWeight: 500, color: '#4B3FA8' }}>Final APA Score</Text>
                            <Text style={{ fontSize: 22, fontWeight: 500, color: '#7B61FF' }}>
                                {resultData.final_apa_point?.toFixed(2)}
                            </Text>
                        </Flex>
                    </Card>
                )}
            </Flex>

            <Flex {...table.header} style={{ marginBottom: 16 }}>
                <CustomDatePicker selectedYear={selectedYear} handleSelectedYear={handleSelectedYear} />
            </Flex>

            {filteredForms?.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 40, color: '#8B8FA8' }}>
                    No APA forms available for this faculty in {selectedYear}.
                </div>
            ) : (
                <Table
                    columns={columns}
                    dataSource={filteredForms}
                    rowKey="id"
                    pagination={false}
                    style={{ borderRadius: 12, overflow: 'hidden' }}
                    rowClassName={() => 'apa-faculty-row'}
                />
            )}
            <br />
            <br />

            <Flex gap={16} wrap="wrap" style={{ marginBottom: 20 }}>
                <CommentSummaryCard type="head" />
                <CommentSummaryCard type="dean" />
                <CommentSummaryCard type="vc" />
            </Flex>

            <CommentsModal
                open={!!activeModal}
                type={activeModal}
                record={row}                          // row.role must come from API
                comment={commentsStore[activeModal] ?? null}
                onClose={() => setActiveModal(null)}
                onSubmit={(text) => handleAddComment(activeModal, text)}
                currentUserRole={userDetails?.role}   // 'hod' | 'dean' | 'vc'
                currentUserId={userDetails?.user_id}
            />

            <style>{`
        .apa-faculty-row:hover td {
          background: #FAF9FF !important;
        }
        .ant-table-thead > tr > th {
          background: #F7F5FF !important;
          color: #4B3FA8 !important;
          font-weight: 600 !important;
        }
      `}</style>
        </Card>
    );
};

export default FacultyDetails;